import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ARENA_CHEER_COOLDOWN_MS, ARENA_PREDICTION_NOTICE, type ArenaPick, type ArenaPrivate, type ArenaSide, type ArenaState } from "@townsy/contracts";
import { SignUpToPlay } from "../SignUpToPlay";
import type { VenueHudProps } from "../types";
import {
  STAKES,
  asArenaPrivate,
  asArenaState,
  basesText,
  clockText,
  crowdWord,
  downText,
  headline,
  inkOn,
  momentLabel,
  periodText,
  pickLabel,
  picksFor,
  potentialPayout,
  predictionBlocker,
  resultText,
  secondsToKickoff,
  sectionLabel,
} from "./model";
import "./arena.css";

/** Local clock for the countdowns and the cheer cooldown (the server's numbers, counted down here). */
function useNow(periodMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), periodMs);
    return () => window.clearInterval(id);
  }, [periodMs]);
  return now;
}

/** The stadium's state with the local timing derived from it (when it arrived, the cheer cooldown). */
function useArena(props: VenueHudProps) {
  const state = asArenaState(props.shared);
  const mine = asArenaPrivate(props.mine);
  const stateAt = useMemo(() => Date.now(), [props.shared]);
  const mineAt = useMemo(() => Date.now(), [props.mine]);
  const now = useNow(250);
  const t = Math.max(now, stateAt);
  return {
    state,
    mine,
    kickoffSeconds: state ? secondsToKickoff(state, stateAt, t) : 0,
    cheerReadyAt: mine ? mineAt + mine.cheerIn : 0,
    now: t,
  };
}

/**
 * The stadium overlay (ADR-0044): the scoreboard (teams, score, clock or inning, period), the next-game countdown,
 * Cheer buttons and the crowd meter, the prediction panel (pick, stake, your prediction, your result and chips), and
 * Sit with the home or the away fans. It only displays the server's state and sends intents.
 */
export function ArenaHud(props: VenueHudProps) {
  const a = useArena(props);
  return <ArenaHudView {...props} state={a.state} mine={a.mine} kickoffSeconds={a.kickoffSeconds} cheerReadyAt={a.cheerReadyAt} now={a.now} />;
}

/** The collapsed strip: the mini scoreboard, the countdown and the Cheer buttons (and the moment flash). */
export function ArenaCompact(props: VenueHudProps) {
  const a = useArena(props);
  if (!a.state) return null;
  return (
    <div className="arena-compact" data-testid="arena-compact">
      <MomentBanner state={a.state} now={a.now} />
      <MiniBoard state={a.state} kickoffSeconds={a.kickoffSeconds} />
      <CheerButtons state={a.state} send={props.send} cheerReadyAt={a.cheerReadyAt} now={a.now} compact />
    </div>
  );
}

export interface ArenaHudViewProps extends VenueHudProps {
  state: ArenaState | null;
  mine: ArenaPrivate | null;
  /** Seconds to the next kickoff (pre-game and final). */
  kickoffSeconds: number;
  /** Local time when this fan may cheer again. */
  cheerReadyAt: number;
  /** Local time now. */
  now: number;
}

/** The overlay for a given state (separate from the timers so it renders the same on the server in tests). */
export function ArenaHudView({ state, mine, chips, chanceGamesEnabled, guest = false, send, kickoffSeconds, cheerReadyAt, now }: ArenaHudViewProps) {
  if (!state) {
    return (
      <div className="venue-hud-body arena-hud" data-testid="arena-hud">
        <p style={{ margin: 0 }}>Connecting to the stadium...</p>
        <p className="small muted arena-fineprint">{ARENA_PREDICTION_NOTICE}</p>
      </div>
    );
  }
  const head = headline(state, kickoffSeconds);
  const seated = mine?.seat !== null && mine?.seat !== undefined;
  return (
    <div className="venue-hud-body arena-hud" data-testid="arena-hud" data-phase={state.phase} data-sport={state.sport} data-game={state.gameNo}>
      <MomentBanner state={state} now={now} />
      <Scoreboard state={state} />
      <div className="arena-status">
        {/* Announced on phase changes only; the countdown beside it is not a live region. */}
        <strong role="status" aria-live="polite" data-testid="arena-headline">
          {state.phase === "live" ? (state.board.status ? state.board.status : head.title) : head.title}
        </strong>
        <span className="arena-detail" data-testid="arena-countdown">
          {head.detail}
        </span>
      </div>
      <div className="arena-row">
        <CheerButtons state={state} send={send} cheerReadyAt={cheerReadyAt} now={now} />
        <CrowdMeterView state={state} />
      </div>
      <PredictionPanel state={state} mine={mine} chips={chips} chanceGamesEnabled={chanceGamesEnabled} guest={guest} send={send} />
      <div className="arena-row arena-seats" role="group" aria-label="Seats">
        {seated ? (
          <>
            <span className="small" data-testid="arena-seated">
              Sitting with the {mine?.section === "away" ? state.away.name : state.home.name} fans
            </span>
            <button type="button" className="btn btn-sm" onClick={() => send({ type: "sit", section: mine?.section === "away" ? "home" : "away" })} data-testid="arena-switch-section">
              Sit with the {mine?.section === "away" ? state.home.name : state.away.name} fans
            </button>
            <button type="button" className="btn btn-sm" onClick={() => send({ type: "stand" })} data-testid="arena-stand">
              Stand up
            </button>
          </>
        ) : (
          (["home", "away"] as const).map((section: ArenaSide) => (
            <button key={section} type="button" className="btn btn-sm" onClick={() => send({ type: "sit", section })} data-testid={`arena-sit-${section}`}>
              Sit with the {sectionLabel(state, section)}
            </button>
          ))
        )}
      </div>
      {state.feed.length > 0 ? (
        <p className="small muted arena-feed" data-testid="arena-feed">
          {state.feed[state.feed.length - 1]}
        </p>
      ) : null}
      <details className="small muted arena-fineprint">
        <summary>Predictions: play chips only, just for fun</summary>
        Pick the winner (or a draw in soccer) before kickoff with 10, 25, 50 or 100 play chips: a right winner pays 2 times your stake, a right draw 3 times. One prediction per game. If you leave, your prediction still counts and a win is added to your play chips. {ARENA_PREDICTION_NOTICE}
      </details>
    </div>
  );
}

function TeamChip({ team, side }: { team: ArenaState["home"]; side: ArenaSide }) {
  return (
    <span className="arena-team" data-side={side}>
      <span className="arena-swatch" style={{ background: team.primary, borderColor: team.secondary }} aria-hidden="true" />
      <span className="arena-team-name">{team.name}</span>
      <span className="arena-team-short" aria-hidden="true">
        {team.short}
      </span>
    </span>
  );
}

/** The on-screen scoreboard: teams, score, period and clock (or inning, outs, count and bases). */
function Scoreboard({ state }: { state: ArenaState }) {
  const b = state.board;
  const period = periodText(state.sport, b, state.phase);
  const clock = clockText(state.sport, b, state.phase);
  const extra = state.sport === "football" ? downText(b) : state.sport === "baseball" && state.phase === "live" ? basesText(b) : "";
  const score = state.phase === "final" && state.last ? state.last.score : b.score;
  return (
    <div className="arena-board" data-testid="arena-scoreboard" aria-label={`${state.home.name} ${score[0]}, ${state.away.name} ${score[1]}, ${period}${clock ? ` ${clock}` : ""}`}>
      <TeamChip team={state.home} side="home" />
      <span className="arena-score" data-testid="arena-score-home" style={{ background: state.home.primary, color: inkOn(state.home.primary) }}>
        {score[0]}
      </span>
      <span className="arena-score-sep" aria-hidden="true">
        –
      </span>
      <span className="arena-score" data-testid="arena-score-away" style={{ background: state.away.primary, color: inkOn(state.away.primary) }}>
        {score[1]}
      </span>
      <TeamChip team={state.away} side="away" />
      <span className="arena-clock">
        <span data-testid="arena-period">{period}</span>
        {clock ? (
          <span className="arena-clock-time" data-testid="arena-clock">
            {clock}
          </span>
        ) : null}
        {state.phase === "live" && b.possession !== null && state.sport !== "baseball" ? (
          <span className="arena-possession" title={`${b.possession === 0 ? state.home.name : state.away.name} have the ${state.sport === "hockey" ? "puck" : "ball"}`} aria-hidden="true">
            {b.possession === 0 ? "◀" : "▶"}
          </span>
        ) : null}
      </span>
      {extra ? (
        <span className="arena-extra small" data-testid="arena-extra">
          {extra}
        </span>
      ) : null}
    </div>
  );
}

function MiniBoard({ state, kickoffSeconds }: { state: ArenaState; kickoffSeconds: number }) {
  const score = state.phase === "final" && state.last ? state.last.score : state.board.score;
  const head = headline(state, kickoffSeconds);
  return (
    <span className="arena-mini" data-testid="arena-mini">
      <strong>
        {state.home.short} {score[0]} – {score[1]} {state.away.short}
      </strong>{" "}
      <span className="arena-detail">{state.phase === "live" ? head.detail : state.phase === "pregame" ? `Kickoff ${head.detail}` : head.detail}</span>
    </span>
  );
}

function CheerButtons({ state, send, cheerReadyAt, now, compact }: { state: ArenaState; send: VenueHudProps["send"]; cheerReadyAt: number; now: number; compact?: boolean }) {
  // A click starts the cooldown at once (the server's answer confirms it).
  const [localReadyAt, setLocalReadyAt] = useState(0);
  const readyAt = Math.max(cheerReadyAt, localReadyAt);
  const waiting = readyAt > now;
  const cheer = (team: ArenaSide) => {
    if (send({ type: "cheer", team })) setLocalReadyAt(Date.now() + ARENA_CHEER_COOLDOWN_MS);
  };
  const reason = waiting ? "Catch your breath: you can cheer again in a moment." : undefined;
  return (
    <span className="arena-cheers" role="group" aria-label="Cheer">
      {(["home", "away"] as const).map((side) => {
        const team = side === "home" ? state.home : state.away;
        return (
          <button
            key={side}
            type="button"
            className="btn btn-sm arena-cheer"
            style={{ background: team.primary, color: inkOn(team.primary), borderColor: team.secondary }}
            disabled={waiting}
            title={reason}
            onClick={() => cheer(side)}
            data-testid={`arena-cheer-${side}`}
          >
            {compact ? `Cheer ${team.short}` : `Cheer for ${team.name}`}
          </button>
        );
      })}
    </span>
  );
}

function CrowdMeterView({ state }: { state: ArenaState }) {
  const { home, away } = state.crowd;
  return (
    <span className="arena-crowd" aria-label={`Crowd: ${crowdWord(Math.max(home, away))}`} data-testid="arena-crowd">
      <span className="small arena-crowd-label">Crowd: {crowdWord(Math.max(home, away))}</span>
      <span className="arena-crowd-bars" aria-hidden="true">
        <span className="arena-crowd-bar" data-testid="arena-crowd-home" data-level={home} style={{ width: `${home / 2}%`, background: state.home.primary }} />
        <span className="arena-crowd-bar arena-crowd-away" data-testid="arena-crowd-away" data-level={away} style={{ width: `${away / 2}%`, background: state.away.primary }} />
      </span>
    </span>
  );
}

function PredictionPanel({
  state,
  mine,
  chips,
  chanceGamesEnabled,
  guest,
  send,
}: {
  state: ArenaState;
  mine: ArenaPrivate | null;
  chips: number | null;
  chanceGamesEnabled: boolean;
  guest: boolean;
  send: VenueHudProps["send"];
}) {
  const [pick, setPick] = useState<ArenaPick | null>(null);
  const [stake, setStake] = useState<number>(STAKES[0]);
  // A new game clears the pick (adjusting state while rendering, as React recommends for derived resets).
  const [game, setGame] = useState(state.gameNo);
  if (game !== state.gameNo) {
    setGame(state.gameNo);
    setPick(null);
  }
  // A guest while predictions are on (ADR-0048: they need an account): "Sign up (18+) to play" instead of the form.
  const signUp = guest && chanceGamesEnabled;
  const blocker = predictionBlocker({ state, mine, chips, chanceGamesEnabled, guest, pick, stake });
  const open = chanceGamesEnabled && !signUp && state.phase === "pregame" && state.predictionsOpen && !(mine?.prediction && mine.prediction.gameNo === state.gameNo);
  const mineNow = mine?.prediction && mine.prediction.gameNo === state.gameNo ? mine.prediction : null;
  const last = mine?.last && state.last && mine.last.gameNo === state.last.gameNo ? mine.last : null;
  return (
    <div className="arena-predict" role="group" aria-label="Predict the winner with play chips" data-testid="arena-predict-panel">
      {last ? (
        <p className={`notice small arena-result ${last.won > 0 ? "notice-success" : ""}`} data-testid="arena-my-result">
          {last.won > 0 ? `You won ${last.won} play chips!` : "No win this time."} {resultText(state, last.outcome)}; you picked {pickLabel(state, last.pick).toLowerCase()} for {last.stake}.
        </p>
      ) : null}
      {mineNow ? (
        <p className="small arena-mine" data-testid="arena-my-prediction">
          Your prediction: <strong>{pickLabel(state, mineNow.pick)}</strong> for {mineNow.stake} play chips (pays {mineNow.payout} if right).
        </p>
      ) : null}
      {mineNow === null && signUp ? (
        <SignUpToPlay watch="Guests can watch, sit with the fans and cheer." />
      ) : mineNow === null ? (
        <div className="arena-row">
          <div className="segmented arena-picks" role="group" aria-label="Your pick">
            {picksFor(state.sport).map((p) => (
              <button key={p} type="button" className="btn btn-sm" aria-pressed={open ? pick === p : undefined} disabled={!open} onClick={() => setPick(pick === p ? null : p)} data-testid={`arena-pick-${p}`}>
                {pickLabel(state, p)}
                <span className="arena-odds"> {p === "draw" ? "3x" : "2x"}</span>
              </button>
            ))}
          </div>
          <div className="segmented arena-stakes" role="group" aria-label="Stake in play chips">
            {STAKES.map((s) => (
              <button key={s} type="button" className="btn btn-sm" aria-pressed={stake === s} disabled={!open || (chips !== null && s > chips)} onClick={() => setStake(s)} data-testid={`arena-stake-${s}`}>
                {s}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={blocker !== ""}
            aria-describedby={blocker ? "arena-predict-hint" : undefined}
            onClick={() => {
              if (pick !== null && send({ type: "predict", pick, stake })) setPick(null);
            }}
            data-testid="arena-predict"
          >
            {pick !== null && open ? `Predict ${pickLabel(state, pick).toLowerCase()} for ${stake} (pays ${potentialPayout(pick, stake)})` : "Predict"}
          </button>
        </div>
      ) : null}
      {blocker && mineNow === null && !signUp ? (
        <p id="arena-predict-hint" className="small muted arena-hint" data-testid="arena-predict-hint">
          {blocker}
        </p>
      ) : null}
    </div>
  );
}

/** How long a big moment flashes over the room (ms). */
export const ARENA_MOMENT_MS = 3500;

/** The flash itself (exported for the tests; MomentBanner shows it over the room). */
export function MomentBannerView({ state }: { state: ArenaState }) {
  const m = state.moment;
  if (!m) return null;
  const team = m.team === 0 ? state.home : m.team === 1 ? state.away : null;
  const bg = team?.primary ?? "#14171F";
  return (
    <div className="arena-moment" role="status" aria-live="assertive" style={{ background: bg, color: inkOn(bg), borderColor: team?.secondary ?? "#F2B92C" }} data-testid="arena-moment" data-kind={m.kind}>
      <strong className="arena-moment-title">{momentLabel(m.kind)}</strong>
      <span className="arena-moment-detail">{m.text}</span>
    </div>
  );
}

/**
 * A big moment flashes over the room for everyone: the server's latest moment, once per id, for ARENA_MOMENT_MS from
 * when it happened (by the server's clock in the state). Mounted by both the full overlay and the compact strip.
 */
export function MomentBanner({ state, now }: { state: ArenaState; now: number }) {
  const m = state.moment;
  // When this moment happened on the local clock (its age when the state was sent, counted on locally).
  const seen = useRef<{ id: number; at: number } | null>(null);
  if (m && seen.current?.id !== m.id) seen.current = { id: m.id, at: now - Math.max(0, state.now - m.at) };
  if (!m || !seen.current || now - seen.current.at > ARENA_MOMENT_MS || typeof document === "undefined") return null;
  return createPortal(<MomentBannerView key={m.id} state={state} />, document.body);
}
